import * as m from '@staiter/i18n';
import type { InvoiceLine } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { Tier3SendAction } from '~/components/approvals';
import { Breadcrumbs, PageHeader, Table, type TableColumn } from '~/components/data';
import { PeriodCloseBanner } from '~/components/domain';
import { ProjectRefChip } from '~/components/domain/project/ProjectRefChip';
import { Button } from '~/components/primitives';
import { cancelInvoice, fetchInvoice, postInvoice } from '~/lib/api/invoices';
import { formatDate, formatMoney, formatQty } from '~/lib/format';
import { canDo, useUserScope } from '~/lib/rbac/use-user-scope';
import shared from './_auth._shared.module.css';

/**
 * S14-FE.5 — Invoice detail page (universal AR/AP).
 */

export const Route = createFileRoute('/_auth/invoices/$id/')({
  staticData: { minMainWidth: 960 },
  component: InvoiceDetailPage,
});

function InvoiceDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['invoices', id],
    queryFn: () => fetchInvoice(id),
    refetchInterval: 5_000,
  });

  const postMut = useMutation({
    mutationFn: () => postInvoice(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['invoices'] }),
  });
  const cancelMut = useMutation({
    mutationFn: () => cancelInvoice(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['invoices'] }),
  });

  // FE Pass 30: line columns localized (was '№' / 'VAT' hardcoded + counterparty
  // header reused для description + total header reused для price).
  const lineColumns: TableColumn<InvoiceLine>[] = useMemo(
    () => [
      {
        id: 'no',
        header: m.invoice_column_line_no(),
        accessor: (r) => String(r.line_no),
        width: '60px',
        align: 'right',
        mono: true,
      },
      {
        id: 'desc',
        header: m.invoice_column_description(),
        accessor: (r) => r.description_ru,
      },
      {
        id: 'qty',
        header: m.invoice_column_qty(),
        // FE-N Pass 73: formatQty вместо raw «3.000000» (trim trailing zeros + UoM).
        accessor: (r) => formatQty(r.qty, r.uom_code),
        mono: true,
        align: 'right',
      },
      {
        id: 'price',
        header: m.invoice_column_unit_price(),
        accessor: (r) => formatMoney(r.unit_price),
        mono: true,
        align: 'right',
      },
      {
        id: 'vat',
        header: m.invoice_column_vat(),
        // FE-N Pass 73: «12» → «12%» (numeric VAT rate). exempt rendered as is.
        accessor: (r) => (r.vat_rate === 'exempt' ? r.vat_rate : `${r.vat_rate}%`),
        width: '80px',
        align: 'right',
        mono: true,
      },
      {
        id: 'total',
        header: m.invoice_column_line_total(),
        accessor: (r) => formatMoney(r.line_total),
        mono: true,
        align: 'right',
      },
    ],
    [],
  );

  const scope = useUserScope();
  // BE-10 (tails-sweep-2): `invoice` заведён в каноне ABILITIES. До этого гейт
  // висел на `sales_order` как на «ближайшей коммерческой сущности» — то есть
  // право на счёт определялось правом на заказ.
  const canEdit = canDo(scope, 'invoice', 'update');
  const inv = query.data;
  if (!inv) {
    return (
      <div>
        <PageHeader title={m.phase2_invoices_ar_title()} />
        <div>{query.isLoading ? '…' : 'Not found'}</div>
      </div>
    );
  }

  const canPost = inv.state === 'draft';
  const canCancel =
    inv.state !== 'paid' &&
    inv.state !== 'partially_paid' &&
    inv.state !== 'cancelled' &&
    inv.state !== 'void';

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs
            items={[
              {
                label:
                  inv.kind === 'ar' ? m.phase2_invoices_ar_title() : m.phase2_invoices_ap_title(),
                to: inv.kind === 'ar' ? '/invoices/ar' : '/invoices/ap',
              },
              { label: inv.document_number },
            ]}
          />
        }
        title={`${inv.kind === 'ar' ? m.phase2_invoices_ar_title() : m.phase2_invoices_ap_title()} · ${inv.document_number}`}
        description={
          (inv as { project_id?: string | null }).project_id ? (
            <ProjectRefChip
              projectId={(inv as { project_id?: string | null }).project_id ?? null}
            />
          ) : undefined
        }
        actions={
          <>
            {canPost && canEdit && (
              <Link to="/invoices/$id/edit" params={{ id }}>
                <Button variant="secondary">{m.common_button_edit()}</Button>
              </Link>
            )}
            {canPost && (
              <Button onClick={() => postMut.mutate()} loading={postMut.isPending}>
                {m.invoice_button_post()}
              </Button>
            )}
            {canCancel && (
              <Button
                variant="ghost"
                onClick={() => cancelMut.mutate()}
                loading={cancelMut.isPending}
              >
                {m.invoice_button_cancel()}
              </Button>
            )}
            <Tier3SendAction
              label={m.invoice_button_push_1c()}
              subject={{ taskType: 'documents_push_1c', subjectKind: 'invoice', payload: { id } }}
            />
          </>
        }
      />
      <PeriodCloseBanner scope="invoice" />

      <div className={shared.statGrid4Lg}>
        <div>
          <div className={shared.statLabel}>{m.invoice_column_issue_date()}</div>
          <div>{formatDate(inv.issue_date)}</div>
        </div>
        <div>
          <div className={shared.statLabel}>{m.invoice_column_due_date()}</div>
          <div>{formatDate(inv.due_date)}</div>
        </div>
        <div>
          <div className={shared.statLabel}>{m.invoice_column_total()}</div>
          <div data-testid="invoice-total">{formatMoney(inv.total)}</div>
        </div>
        <div>
          <div className={shared.statLabel}>{m.invoice_column_outstanding()}</div>
          <div data-testid="invoice-outstanding">{formatMoney(inv.outstanding_amount)}</div>
        </div>
      </div>

      {/* FE-N Pass 73: detail card расширен — counterparty + state + notes + 1C ref + subtotal/VAT. */}
      <div className={shared.detailCard}>
        {/* access-control (F9) — компания записи. */}
        <div className={shared.detailRow}>
          <span className={shared.detailRowLabel}>{m.cp_field_company()}</span>
          <strong>{(inv as { entity_name?: string | null }).entity_name ?? '—'}</strong>
        </div>
        <div className={shared.detailRow}>
          <span className={shared.detailRowLabel}>{m.invoice_column_counterparty()}</span>
          <strong>
            {(inv as { counterparty_name_ru?: string | null }).counterparty_name_ru ??
              (inv as { counterparty_name_en?: string | null }).counterparty_name_en ??
              inv.counterparty_id.slice(0, 8)}
          </strong>
        </div>
        <div className={shared.detailRow}>
          <span className={shared.detailRowLabel}>{m.invoice_column_state()}</span>
          {/* FE-89: StatusBadge pill — bg/color по state group (paid=ok, cancelled/void=danger). */}
          {/* FE-89 honest-audit: icon-inside-pill. */}
          {(() => {
            const isOk =
              inv.state === 'paid' || inv.state === 'partially_paid' || inv.state === 'posted';
            const isDanger = inv.state === 'cancelled' || inv.state === 'void';
            const icon = isOk ? '✓' : isDanger ? '✗' : '◑';
            return (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '2px 10px',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 500,
                  background: isOk
                    ? 'var(--ok-bg-15)'
                    : isDanger
                      ? 'var(--danger-bg-15)'
                      : 'var(--warn-bg-15)',
                  color: isOk
                    ? 'var(--color-ok)'
                    : isDanger
                      ? 'var(--color-danger)'
                      : 'var(--color-warn)',
                }}
              >
                <span aria-hidden="true">{icon}</span>
                {m.invoice_state_label({ state: inv.state })}
              </span>
            );
          })()}
        </div>
        <div className={shared.detailRow}>
          <span className={shared.detailRowLabel}>{m.invoice_column_subtotal()}</span>
          <strong>{formatMoney(inv.subtotal)}</strong>
        </div>
        <div className={shared.detailRow}>
          <span className={shared.detailRowLabel}>{m.invoice_column_vat_total()}</span>
          <strong>{formatMoney(inv.vat_total)}</strong>
        </div>
        {inv.notes_ru && (
          <div className={shared.detailRow}>
            <span className={shared.detailRowLabel}>{m.invoice_detail_notes()}</span>
            <strong>{inv.notes_ru}</strong>
          </div>
        )}
        {inv.onec_ref && (
          <div className={shared.detailRow}>
            <span className={shared.detailRowLabel}>{m.invoice_detail_onec_ref()}</span>
            <strong>{inv.onec_ref}</strong>
          </div>
        )}
      </div>

      <Table<InvoiceLine>
        data={inv.lines}
        columns={lineColumns}
        rowKey={(r) => String(r.line_no)}
        ariaLabel="invoice lines"
        emptyTitle={m.invoice_empty_title()}
      />
      {/* EFE-13: last-modified footer with link to audit log */}
      <div className={shared.subtle} style={{ marginTop: 'var(--space-3)' }}>
        {m.detail_last_updated()}: {formatDate(inv.updated_at ?? inv.created_at)}
        {' · '}
        <a href={`/admin/audit?entity_id=${id}`} className={shared.link}>
          {m.detail_view_audit_log()}
        </a>
      </div>
    </div>
  );
}
